/**
 * 업로드 행 검증 로직.
 * - 전화번호 유효성
 * - 주소 형식 (업체 권역 소속 / 법정동 추출 가능 여부)
 * - 파일 내/파일 간 중복 (학교+이름+주소 기준)
 * - 필수 필드 (학교, 이름, 주소)
 */
import type { Student } from "@/types";

import { isInRegion, parseGuFrom, sidoShort } from "./region";
export type IssueLevel = "error" | "warning" | "info";

export interface Issue {
  level: IssueLevel;
  field?: keyof Student | "general";
  code: string;
  message: string;
}

const PHONE_VALID = /^(01[016789])-?\d{3,4}-?\d{4}$/;

// ── 주소 판정 헬퍼 ────────────────────────────────────────────────
// excelParse의 stripRoad·auditRoadMismatch·checkDataHealth와 같은 규칙이다.
// (excelParse가 이 파일의 normalizePhone을 import하므로 역방향 import는 순환참조가 된다 → 자체 정의)
// 한쪽 규칙을 고치면 나머지도 같이 고칠 것.
function stripRoad(t: string): string {
  return t.replace(/[가-힣A-Za-z0-9·]*(?:대로|로|길)\s*\d+(?:\s*번\s*[가-힣]?\s*가?길\s*\d+)?(?:-\d+)?(?:번지)?/g, " ");
}
function roadKeys(s: string): string[] {
  const out: string[] = [];
  const re = /([가-힣A-Za-z0-9·]+(?:대로|로))\s*(\d+)\s*번\s*([가-힣]?)\s*길/g;
  let m;
  while ((m = re.exec(s || ""))) out.push(`${m[1]}${m[2]}번${m[3]}길`);
  return out;
}
const sameRoad = (a: string, b: string) => a === b || a.endsWith(b) || b.endsWith(a);
/** 도로명(…대로·…로·…길, 번길 포함)만 뽑는다 — "범어로 5"가 "범어로12길 5"로 바뀐 것을 잡기 위해(3차 예행연습 4번) */
function roadNames(s: string): string[] {
  const out: string[] = [];
  // 탐욕 매칭이어야 "범어로12길"이 "범어로"로 잘리지 않는다
  const re = /([가-힣A-Za-z0-9·]+(?:대로|로|길))(?=\s*\d)/g;
  let m;
  while ((m = re.exec(s || ""))) out.push(m[1]);
  return out;
}

/**
 * 사람이 확인해야 통과시킬 수 있는 경고 코드.
 * 이 항목들은 "그냥 저장하면 조용히 틀린 채로 몇 달이 간다"는 성질이라 업로드를 한 번 막는다.
 * (2026-08-15: 6월 최초 업로드에서 샌 16건이 두 달간 발견되지 않았던 문제)
 */
export const CONFIRM_CODES = new Set(["lost_unit", "lost_dong", "road_lost", "road_mismatch"]);

/** 확인 없이는 올리면 안 되는 항목이 있는가 */
export function needsConfirm(issues: Issue[]): boolean {
  return issues.some((i) => CONFIRM_CODES.has(i.code));
}

export function validateRow(r: Partial<Student>, allRows: Partial<Student>[]): Issue[] {
  const issues: Issue[] = [];

  if (!r.school) issues.push({ level: "error", field: "school", code: "missing_school", message: "학교명 없음" });
  if (!r.name) issues.push({ level: "error", field: "name", code: "missing_name", message: "학생명 없음" });
  if (!r.rawAddress && !r.refinedAddress) issues.push({ level: "error", field: "rawAddress", code: "missing_address", message: "주소 없음" });

  // 전화번호
  if (!r.phone) {
    issues.push({ level: "warning", field: "phone", code: "missing_phone", message: "전화번호 없음 - 보호자 알림 문자를 못 보냅니다" });
  } else {
    const parts = r.phone.split(",").map((p) => p.trim()).filter(Boolean);
    const bad = parts.filter((p) => !PHONE_VALID.test(p));
    if (bad.length) {
      issues.push({
        level: "warning",
        field: "phone",
        code: "bad_phone",
        message: `전화번호 형식 이상: ${bad.join(", ")}`,
      });
    }
  }

  // 주소 — 업체 권역(시·도) 소속인지, 동 추출 되는지. 부산 고정이던 것을 업체 설정 기준으로(예행연습 02-24)
  const addr = r.refinedAddress || r.rawAddress || "";
  // 구가 있으면 그 구로, 없으면 주소에서 구를 읽어 권역(시·도·시군구 목록) 안인지 본다 — 구만 있다고 통과시키면 다른 시·도 주소가 새어 들어온다(코덱스 검토)
  const inRegion = isInRegion(addr, r.gu || parseGuFrom(addr));
  if (!inRegion && addr) {
    issues.push({ level: "warning", field: "rawAddress", code: "non_region", message: `${sidoShort()} 주소 아님 - 확인 필요` });
  }
  if (addr && !/(\d+-?\d*|\d+번길|\d+로|\d+길)/.test(addr)) {
    issues.push({ level: "warning", field: "rawAddress", code: "unclear_address", message: "주소에 번지/도로명 번호가 불명확" });
  }
  if (!r.dong) {
    issues.push({ level: "info", field: "dong", code: "missing_dong", message: "동 이름 못 찾음 - 「주소 자동 정리」를 누르세요" });
  }

  // 좌표 없으면 info
  if (!r.lat || !r.lng) {
    issues.push({ level: "info", field: "lat", code: "no_coords", message: "지도 위치 없음 - 「주소 자동 정리」를 누르세요" });
  }

  // ── 명단 원문의 동·호가 파싱에서 버려졌는지 (함정 #10·#10-1) ──
  // 빈 값으로 저장되면 기사 화면에서 호수가 통째로 사라져 어느 집인지 알 수 없다.
  const rawAddr = (r.rawAddress || "").trim();
  const unitTxt = stripRoad(" " + rawAddr.replace(/[(),]/g, " ") + " ");
  const hasDong = !!(r.buildingDong || "").trim();
  const hasHo = !!(r.buildingHo || "").trim();
  if (rawAddr && !hasDong && !hasHo && /((?<![가-힣])\d+\s*동)|(\d+\s*호)|(\d+\s*층)|([A-Za-z]\s*-\s*\d+)/.test(unitTxt)) {
    issues.push({ level: "warning", field: "buildingHo", code: "lost_unit", message: "명단엔 동/호가 있는데 읽지 못함 - 확인 필요" });
  } else if (rawAddr && !hasDong && /(?:^|[\s,])([A-Za-z])\s*-\s*\d{1,5}/.test(unitTxt)) {
    // "A-449호"처럼 호수만 살아남는 경우 — 호수가 있어 위 조건을 빠져나간다
    issues.push({ level: "warning", field: "buildingDong", code: "lost_dong", message: "명단의 동 표기(A·B 등)를 읽지 못함 - 확인 필요" });
  }

  // ── 정제 주소가 명단과 다른 길로 잡혔는지 (함정 #8·#9) ──
  // 지오코딩 전(refined 비어 있음)에는 판정하지 않는다.
  if (r.refinedAddress && rawAddr) {
    const rk = roadKeys(rawAddr), fk = roadKeys(r.refinedAddress);
    if (rk.length && !fk.length) {
      issues.push({ level: "warning", field: "refinedAddress", code: "road_lost", message: `명단의 "${rk[0]}"이 정제 주소에서 사라짐 - 다른 집일 수 있음` });
    } else if (rk.length && fk.length && !rk.some((a) => fk.some((b) => sameRoad(a, b)))) {
      issues.push({ level: "warning", field: "refinedAddress", code: "road_mismatch", message: `명단 "${rk[0]}" ≠ 정제 "${fk[0]}" - 확인 필요` });
    } else if (!rk.length) {
      // 번길이 없는 보통 도로명 — 길 이름 자체가 달라졌으면(범어로 → 범어로12길) 다른 집이다
      const rn = roadNames(rawAddr), fn = roadNames(r.refinedAddress);
      if (rn.length && fn.length && !rn.some((a) => fn.some((b) => sameRoad(a, b)))) {
        issues.push({ level: "warning", field: "refinedAddress", code: "road_mismatch", message: `명단 "${rn[0]}" ≠ 정리 "${fn[0]}" - 다른 길로 잡힘, 확인 필요` });
      }
    }
  }

  // 중복 체크 (같은 school+name이 여러번)
  const dupKey = `${r.school || ""}|${r.name || ""}`;
  const sameCount = allRows.filter((x) => `${x.school || ""}|${x.name || ""}` === dupKey).length;
  if (sameCount > 1) {
    issues.push({ level: "warning", code: "duplicate", message: `동일 (학교+학생명) ${sameCount}건 발견` });
  }

  return issues;
}

/** 행에서 가장 심각도 높은 레벨 반환 */
export function worstLevel(issues: Issue[]): IssueLevel | "ok" {
  if (issues.some((i) => i.level === "error")) return "error";
  if (issues.some((i) => i.level === "warning")) return "warning";
  if (issues.some((i) => i.level === "info")) return "info";
  return "ok";
}

/** 행 ID — 업로드시 Firestore doc id 로도 사용 */
export function rowDocId(r: Partial<Student>): string {
  const school = (r.school || "").replace(/\s/g, "");
  const addr = (r.refinedAddress || r.rawAddress || "").slice(0, 25);
  return `${school}_${r.name}_${addr}`.replace(/[^\w가-힣-]/g, "_");
}

/** 표준 정규화 — 전화번호, 공백 등 */
export function normalizePhone(s: string): string {
  // 한 칸에 번호 둘 — 쉼표·줄바꿈·/·; 로 나눈다. 엑셀 칸의 줄바꿈은 읽을 때 띄어쓰기로 바뀌므로(excelParse cleanStr),
  // 띄어쓰기로 나뉜 조각이 모두 9자리 이상이면 따로 본다("010 1234 5678"처럼 한 번호를 띄어 쓴 것은 그대로 한 번호).
  // 예전에는 줄바꿈을 지운 뒤에 쉼표로 바꾸려 해서 두 번호가 22자리 한 번호로 붙었다(4차 점검 97).
  const parts = (s || "")
    .split(/[,\n\r/;]+/)
    .flatMap((p) => {
      const toks = p.trim().split(/\s+/).filter(Boolean);
      return toks.length > 1 && toks.every((t) => t.replace(/\D/g, "").length >= 9) ? toks : [p];
    })
    .map((p) => p.replace(/\D/g, ""))
    .filter(Boolean);
  return parts
    .map((p) => {
      // 엑셀 숫자 서식으로 손실된 휴대폰 앞자리 0 복원 (1로 시작하는 10자리 → 0 보정)
      const v = p.length === 10 && p.startsWith("1") ? `0${p}` : p;
      if (v.length === 11) return `${v.slice(0, 3)}-${v.slice(3, 7)}-${v.slice(7)}`;
      if (v.length === 10) return `${v.slice(0, 3)}-${v.slice(3, 6)}-${v.slice(6)}`;
      return v;
    })
    .join(",");
}
